import {
  BuildingType,
  ConstructionType,
  Gender,
  HomeType,
  IdType,
  OwnershipType,
  PlanType,
  Race,
} from '@/enums';
import { HomeTypeName } from '@/enums/enum/HomeType';
import {
  createMyRumahProductItems,
  createProductItem,
  googleAnalyticsAddPaymentInfo,
  googleAnalyticsMyRumahBeginQuotation,
  googleAnalyticsMyRumahQuoteButton,
  googleAnalyticsNormalAction,
  googleAnalyticsPurchaseSuccessPage,
  googleAnalyticsQuoteButton,
  googleAnalyticsSubmitDeclare,
  googleAnalyticsWithPersonalDetails,
  googleAnalyticsWithQuotationDetails,
} from '@/helpers/GoogleAnalytic';
import { useAppSelector } from '@/hook';
import { useLocation } from '@reach/router';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import { startCase } from 'lodash';
import { useProductEntity } from './useProductEntity';
import { getRoute } from './useRouteSection';

export function useGoogleAnalytic() {
  const location = useLocation();
  const uiState = useAppSelector((state) => state.uiState);
  const planSummary = useAppSelector((state) => state.planSummary);
  const info = useAppSelector((state) => state.infoState);
  const personalDetail = useAppSelector((state) => state.infoState.personalDetail);
  const quotationDetail = useAppSelector((state) => state.planSummary.quotationDetail);

  const { entity, productType } = getRoute(location.pathname);
  const { getEntityTypeKey, getProductCode, getProductName, getProduct } =
    useProductEntity();
  const product = getProduct();
  const productCode = getProductCode();
  const productName = getProductName();
  const entityType = getEntityTypeKey().toUpperCase();

  const storedData = Cookies.get('myCookie');
  const myCookieData = storedData !== undefined ? JSON.parse(storedData) : null;

  const setCookieWithExpiration = (
    cookieName: string,
    data: any,
    minutes: number,
  ) => {
    const serializedData = JSON.stringify(data);

    const expirationTime = new Date(new Date().getTime() + minutes * 60 * 1000);

    Cookies.set(cookieName, serializedData, {
      expires: expirationTime,
      path: '/',
    });
  };

  // shared variables
  const insuranceType = productType === HomeType.Hohh ? 'Property' : 'Home';
  const planAddOn =
    planSummary.planPrice?.addOn?.map((addOn) => addOn.name).join(', ') ||
    'N/A';
  const ownershipType =
    quotationDetail.ownership != 0
      ? OwnershipType[quotationDetail.ownership]
      : '';
  const propertyType =
    quotationDetail.buildingType != 0
      ? startCase(BuildingType[quotationDetail.buildingType])
      : '';
  const totalPrice = planSummary.planPrice?.totalPremium ?? 0;
  const planTypeNum = quotationDetail.planType;
  let coverageType = '';
  switch (planTypeNum) {
    case PlanType.BuildingContentProtection:
      coverageType = 'Building + Contents';
      break;
    case PlanType.BuildingProtection:
      coverageType = 'Building Only';
      break;
    case PlanType.ContentProtection:
      coverageType = 'Content Only';
      break;
    default:
      break;
  }

  const planPrice = planSummary.planPrice?.netPremium ?? 0;
  const constructionType =
    quotationDetail.constructionType != 0
      ? startCase(ConstructionType[quotationDetail.constructionType])
      : '';
  const coverageStartDate = quotationDetail.startDate;
  const noOfStorey = quotationDetail.noOfStorey;

  const goToExternalPage = () => {
    googleAnalyticsNormalAction(
      'go_to_page',
      product +
        ' - ' +
        startCase(entity) +
        ' - ' +
        OwnershipType[quotationDetail.ownership],
      'Go To Page',
      'External Page - PIDM',
    );
  };

  const myRumahNotEligibleBackBtn = () => {
    googleAnalyticsNormalAction(
      'go_to_home',
      `${HomeTypeName.MyRumah} - ${startCase(entity)} - Not Eligible`,
      'Go To Home',
      'Home',
    );
  };

  const declareOrSkip = (declare: boolean, title: string) => {
    if (declare) {
      googleAnalyticsNormalAction(
        'filter_section',
        `${product} - ${startCase(entity)} - ${
          OwnershipType[quotationDetail.ownership]
        } - Declare Item`,
        'Filter Section',
        title,
      );
    } else {
      googleAnalyticsNormalAction(
        'filter_section',
        `${product} - ${startCase(entity)} - ${
          OwnershipType[quotationDetail.ownership]
        } - Skip Declare Item`,
        'Filter Section',
        title,
      );
    }
  };

  const submitDeclare = (
    maxDeclaredAmount: number,
    totalDeclaredAmount: number,
    undeclaredAmount: number,
  ) => {
    googleAnalyticsSubmitDeclare(
      'home_submit_declare_item',
      `${product} - ${startCase(entity)} - ${
        OwnershipType[quotationDetail.ownership]
      } - Declare Item - Submit Declare Item`,
      'Submit Declare Item',
      'Declare Item',
      `${maxDeclaredAmount}`,
      `${totalDeclaredAmount}`,
      `${undeclaredAmount}`,
    );
  };

  const myRumahBeginQuotation = () => {
    googleAnalyticsMyRumahBeginQuotation(
      'home_begin_quotation',
      `${HomeTypeName.MyRumah} - ${startCase(entity)}`,
      'Begin Quotation',
      'Quotation',
      undefined,
      undefined,
    );
  };

  const faqToggle = (title: string, isOpen: boolean) => {
    googleAnalyticsNormalAction(
      isOpen ? 'expand_section' : 'close_section',
      `${product} - ${startCase(entity)}`,
      isOpen ? 'Expand Section' : 'Close Section',
      title,
    );
  };

  const tabSelection = (selectedKey: string | null) => {
    if (selectedKey) {
      googleAnalyticsNormalAction(
        'tab_to_section',
        `${product} - ${startCase(entity)}`,
        'Tab To Section',
        startCase(selectedKey),
      );
    }
  };

  const viewFullProductDetail = () => {
    googleAnalyticsNormalAction(
      'go_to_page',
      `${product} - ${startCase(entity)}`,
      'Go To Page',
      'View Full Product Details',
    );
  };

  const goToHome = () => {
    googleAnalyticsNormalAction(
      'go_to_home',
      `${product} - ${startCase(entity)}`,
      'Go To Home',
      'Home',
    );
  };

  const selectLanguage = (newLanguage: string) => {
    googleAnalyticsNormalAction(
      'select_language',
      `${product} - ${startCase(entity)}`,
      'Select Language',
      newLanguage,
    );
  };

  const hohhSubmitContentAmount = () => {
    googleAnalyticsNormalAction(
      'home_submit_contents_amount',
      `${HomeTypeName.HOHH} - ${startCase(entity)} - ${
        OwnershipType[quotationDetail.ownership]
      }`,
      'Submit Contents Amount',
      'Contents Amount',
    );
  };

  const viewSummary = (isProceedDeclaration: any) => {
    googleAnalyticsNormalAction(
      'home_view_summary',
      `${product} - ${startCase(entity)} - ${
        OwnershipType[quotationDetail.ownership]
      } - Declare Item${isProceedDeclaration ? ' - Submit Declare Item' : ''}`,
      'View Summary',
      'Summary',
    );
  };

  const hohhSkipDeclare = () => {
    googleAnalyticsNormalAction(
      'home_skip_declare_item',
      `${HomeTypeName.HOHH} -  ${startCase(entity)} - ${
        OwnershipType[quotationDetail.ownership]
      } - Declare Item - Submit Declare Item`,
      'Skip Declare Item',
      'Declare Item',
    );
  };

  const hohhGoToCoverageBenefitPage = () => {
    googleAnalyticsNormalAction(
      'go_to_page',
      `${HomeTypeName.HOHH} - ${startCase(entity)}`,
      'Go To Page',
      'Full Coverage & Benefits Comparison',
    );
  };

  const hohhCostBreakdown = () => {
    googleAnalyticsNormalAction(
      'home_cost_breakdown',
      `${HomeTypeName.HOHH} - ${startCase(entity)} - ${
        OwnershipType[quotationDetail.ownership]
      } - Declare Item - Submit Personal Details`,
      'Cost Breakdown',
      'Cost',
    );
  };

  const hohhbeginQuotation = (
    quotationNo: any,
    noOfStorey: number,
    ownership: number,
    building: number,
    construction: number,
  ) => {
    googleAnalyticsQuoteButton(
      'home_begin_quotation',
      `${HomeTypeName.HOHH} - ${startCase(entity)}`,
      'Begin Quotation',
      'Quotation',
      quotationNo,
      startCase(OwnershipType[ownership]),
      startCase(BuildingType[building]),
      `${noOfStorey}`,
      startCase(ConstructionType[construction]),
      quotationDetail.unoccupied ? 'Yes' : 'No',
      quotationDetail.lossOrDamage ? 'Yes' : 'No',
    );
  };

  const optionalAddOn = (planDetails: any) => {
    if (
      planSummary.planPrice?.addOn &&
      planSummary.planPrice.addOn.length > 0
    ) {
      planSummary.planPrice?.addOn?.forEach((addOn) => {
        const addOnDetails = createProductItem(
          addOn.code,
          addOn.name,
          entityType,
          insuranceType,
          addOn.description,
          ownershipType ?? '',
          propertyType ?? '',
          'MYR',
          addOn.addOnPremium,
          1,
          'N/A',
          coverageType ?? '',
        );

        planDetails.push(addOnDetails);
      });
    }
  };

  const addToCart = () => {
    let planDetails = [];
    const plan = createProductItem(
      productCode,
      productName,
      entityType,
      insuranceType,
      planAddOn,
      ownershipType ?? '',
      propertyType ?? '',
      'MYR',
      planSummary.planPrice?.netPremium ?? 0,
      1,
      'N/A',
      coverageType ?? '',
    );

    planDetails.push(plan);

    optionalAddOn(planDetails);

    googleAnalyticsWithQuotationDetails(
      'add_to_cart',
      product +
        ' - ' +
        startCase(entity) +
        ' - ' +
        OwnershipType[quotationDetail.ownership],
      'Add To Cart',
      productName,
      'MYR',
      totalPrice,
      planDetails,
    );
  };

  const myRumahAddToCart = async (
    unoccupied: boolean,
    lossOrDamage: boolean,
    ownership: boolean,
  ) => {
    const idType = IdType[personalDetail.idType];
    const idNumber = personalDetail.idNumber;
    const hashIdNumber = CryptoJS.SHA256(idNumber).toString();
    const nationality =
      uiState.nationalityList.find((x) => x.id == personalDetail.nationality)
        ?.name ?? 'N/A';
    const birthDate = personalDetail.dateOfBirth;
    const unoccupied90Days = unoccupied ? 'Yes' : 'No';
    const sufferedAnyLoss = lossOrDamage ? 'Yes' : 'No';
    const ownershipOption = ownership ? 'Yes' : 'No';

    let planDetails = [];
    const plan = createMyRumahProductItems(
      productCode,
      productName,
      entityType,
      insuranceType,
      'N/A',
      ownershipType,
      propertyType,
      'MYR',
      planPrice,
      1,
      'N/A',
      undefined,
      constructionType,
      coverageStartDate,
      `${noOfStorey}`,
    );

    planDetails.push(plan);

    googleAnalyticsMyRumahQuoteButton(
      'add_to_cart',
      `${HomeTypeName.MyRumah} - ${startCase(entity)}`,
      'Add To Cart',
      `${HomeTypeName.MyRumah}`,
      idType,
      hashIdNumber,
      nationality,
      birthDate,
      unoccupied90Days,
      sufferedAnyLoss,
      ownershipOption,
      'MYR',
      totalPrice,
      planDetails,
    );
  };

  const beginCheckout = async () => {
    let planDetails = [];
    if (productType === HomeType.Hohh) {
      const plan = createProductItem(
        productCode,
        productName,
        entityType,
        insuranceType,
        planAddOn,
        ownershipType,
        propertyType,
        'MYR',
        planPrice,
        1,
        'N/A',
        coverageType,
      );

      planDetails.push(plan);

      optionalAddOn(planDetails);

      googleAnalyticsWithQuotationDetails(
        'begin_checkout',
        `${HomeTypeName.HOHH} - ${startCase(entity)} - ${
          OwnershipType[planSummary.quotationDetail.ownership]
        } - Declare Item - Submit Personal Details`,
        'Begin Checkout',
        `${productName}`,
        'MYR',
        totalPrice,
        planDetails,
      );
    } else {
      const hashName = CryptoJS.SHA256(personalDetail.name || '').toString();
      const idType = IdType[personalDetail.idType];
      const idNumber = personalDetail.idNumber;
      const hashIdNumber = CryptoJS.SHA256(idNumber || '').toString();
      const nationality =
        uiState.nationalityList?.find((n) => n.id == personalDetail.nationality)
          ?.name ?? 'N/A';
      const birthDate = personalDetail.dateOfBirth;
      const gender = personalDetail.gender === Gender.M ? 'Male' : 'Female';
      const race =
        personalDetail.ethnicity !== undefined
          ? Race[personalDetail.ethnicity]
          : '';
      const hashPhoneNumber = CryptoJS.SHA256(
        personalDetail.mobileNo || '',
      ).toString();
      const hashEmail = CryptoJS.SHA256(personalDetail.email || '').toString();

      const hashPropertyAddress = CryptoJS.SHA256(
        JSON.stringify(info.propertyDetail || {}),
      ).toString();
      const hashMailingAddress = CryptoJS.SHA256(
        JSON.stringify(info.mailingDetail || {}),
      ).toString();
      const bankName =
        uiState.bankList?.find((n) => n.id == info.bankAccountDetail.bankId)
          ?.bankName ?? '';
      const hashAccountNumber = CryptoJS.SHA256(
        info.bankAccountDetail.accountNo || '',
      ).toString();

      const plan = createMyRumahProductItems(
        productCode,
        productName,
        entityType,
        insuranceType,
        planAddOn,
        ownershipType,
        propertyType,
        'MYR',
        planPrice,
        1,
        'N/A',
        undefined,
        constructionType,
        coverageStartDate,
        `${noOfStorey}`,
      );

      planDetails.push(plan);

      googleAnalyticsWithPersonalDetails(
        'begin_checkout',
        `${HomeTypeName.MyRumah} - ${startCase(entity)} - Personal Details`,
        'Begin Checkout',
        `${productName}`,
        `${planSummary.planPrice?.quotationNo}`,
        hashName,
        idType,
        hashIdNumber,
        nationality,
        birthDate,
        gender,
        race,
        hashPhoneNumber,
        hashEmail,
        hashPropertyAddress,
        hashMailingAddress,
        bankName,
        hashAccountNumber,
        'MYR',
        totalPrice,
        planDetails,
      );
    }
  };

  const addPaymentInfo = () => {
    const paymentType = planSummary.payMode;

    let planDetails = [];
    if (productType === HomeType.Hohh) {
      const plan = createProductItem(
        productCode,
        productName,
        entityType,
        insuranceType,
        planAddOn,
        ownershipType ?? '',
        propertyType ?? '',
        'MYR',
        planPrice,
        1,
        'N/A',
        coverageType ?? '',
      );

      planDetails.push(plan);

      optionalAddOn(planDetails);

      googleAnalyticsAddPaymentInfo(
        'add_payment_info',
        `${HomeTypeName.HOHH} - ${startCase(entity)} - ${
          OwnershipType[planSummary.quotationDetail.ownership]
        } - Declare Item - Submit Payment Info`,
        'Add Payment Info',
        `${productName}`,
        'MYR',
        totalPrice,
        'N/A',
        paymentType,
        planDetails,
      );
    } else {
      const plan = createMyRumahProductItems(
        productCode,
        productName,
        entityType,
        insuranceType,
        planAddOn,
        ownershipType,
        propertyType,
        'MYR',
        planPrice,
        1,
        'N/A',
        undefined,
        constructionType,
        coverageStartDate,
        `${noOfStorey}`,
      );

      planDetails.push(plan);

      googleAnalyticsAddPaymentInfo(
        'add_payment_info',
        `${HomeTypeName.MyRumah} - ${startCase(entity)} - Payment Method`,
        'Add Payment Info',
        `${productName}`,
        'MYR',
        totalPrice,
        'N/A',
        paymentType,
        planDetails,
      );
    }

    const dataToStore = {
      planDetails,
      totalPrice,
    };

    setCookieWithExpiration('myCookie', dataToStore, 15);
  };

  const successPurchase = (transactionNo: string, policyNo: any) => {
    const planDetails = myCookieData.planDetails;
    const totalPrice = myCookieData.totalPrice;
    const updatePolicyNoArray = planDetails.map((item: any) => {
      return {
        ...item,
        epolicy_number: policyNo[0],
      };
    });

    if (myCookieData != null) {
      if (productType === HomeType.Hohh) {
        googleAnalyticsPurchaseSuccessPage(
          'purchase',
          `${product} - ${startCase(entity)} - ${
            planDetails[0].item_category3
          } - Declare Item - Purchase Success`,
          'Purchase',
          `${planDetails[0].item_name}`,
          undefined, // profit
          'MYR',
          totalPrice,
          'N/A',
          transactionNo,
          updatePolicyNoArray,
        );
      } else {
        googleAnalyticsPurchaseSuccessPage(
          'purchase',
          `${product} - ${startCase(entity)} - Purchase Success`,
          'Purchase',
          `${planDetails[0].item_name}`,
          undefined, // profit
          'MYR',
          totalPrice,
          'N/A',
          transactionNo,
          updatePolicyNoArray,
        );
      }
    }
  };

  return {
    goToHome,
    selectLanguage,
    goToExternalPage,
    hohhbeginQuotation,
    hohhSubmitContentAmount,
    viewSummary,
    addToCart,
    myRumahAddToCart,
    myRumahNotEligibleBackBtn,
    beginCheckout,
    declareOrSkip,
    hohhSkipDeclare,
    submitDeclare,
    myRumahBeginQuotation,
    faqToggle,
    tabSelection,
    viewFullProductDetail,
    hohhGoToCoverageBenefitPage,
    addPaymentInfo,
    hohhCostBreakdown,
    successPurchase,
  };
}
